import React, { useEffect, useState } from "react";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";
import { fetchWithAuth } from "services/api";

interface Questionnaire {
  id: number;
  title: string;
  is_completed: number;
  completed_at: string;
  assignment_id: number;
}

interface Assignment {
  id: number;
  completed_at: string;
  created_at: string;
  questionnaires: Questionnaire[];
}

interface CreateReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: number;
  onSuccess?: () => void;
}

interface SelectedItems {
  [assignmentId: number]: {
    all: boolean;
    questionnaires: Set<number>;
  };
}

interface CreateReportPayload {
  patient_id: number;
  title: string | null;
  prompt: string | null;
  professional_notes: string | null;
  questionnaire_assignments: number[];
}

const CreateReportModal: React.FC<CreateReportModalProps> = ({
  isOpen,
  onClose,
  patientId,
  onSuccess,
}) => {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [expandedAssignments, setExpandedAssignments] = useState<Set<number>>(
    new Set()
  );
  const [selectedItems, setSelectedItems] = useState<SelectedItems>({});
  const [title, setTitle] = useState("");
  const [prompt, setPrompt] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        setLoading(true);
        const response = await fetchWithAuth(
          `/patients/${patientId}/available-assignments`
        );
        const data = response;

        if (data.success) {
          setAssignments(data.data);
          // Initialize selected items state
          const initialSelectedItems: SelectedItems = {};
          data.data.forEach((assignment: Assignment) => {
            initialSelectedItems[assignment.id] = {
              all: false,
              questionnaires: new Set(),
            };
          });
          setSelectedItems(initialSelectedItems);
        } else {
          setError(data.message);
        }
      } catch (err) {
        setError("Error al cargar las asignaciones disponibles");
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchAssignments();
    }
  }, [isOpen, patientId]);

  const toggleAssignment = (assignmentId: number) => {
    setExpandedAssignments((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(assignmentId)) {
        newSet.delete(assignmentId);
      } else {
        newSet.add(assignmentId);
      }
      return newSet;
    });
  };

  const toggleAssignmentSelection = (assignmentId: number) => {
    setSelectedItems((prev) => {
      const newState = { ...prev };
      const assignment = assignments.find((a) => a.id === assignmentId);
      if (!assignment) return prev;

      const isCurrentlyAll = newState[assignmentId].all;
      newState[assignmentId] = {
        all: !isCurrentlyAll,
        questionnaires: !isCurrentlyAll
          ? new Set(assignment.questionnaires.map((q) => q.id))
          : new Set(),
      };
      return newState;
    });
  };

  const toggleQuestionnaireSelection = (
    assignmentId: number,
    questionnaireId: number
  ) => {
    setSelectedItems((prev) => {
      const newState = { ...prev };
      const currentQuestionnaires = new Set(prev[assignmentId].questionnaires);

      if (currentQuestionnaires.has(questionnaireId)) {
        currentQuestionnaires.delete(questionnaireId);
      } else {
        currentQuestionnaires.add(questionnaireId);
      }

      const assignment = assignments.find((a) => a.id === assignmentId);
      const allSelected = assignment
        ? assignment.questionnaires.every((q) =>
            currentQuestionnaires.has(q.id)
          )
        : false;

      newState[assignmentId] = {
        all: allSelected,
        questionnaires: currentQuestionnaires,
      };
      return newState;
    });
  };

  const getSelectedQuestionnaires = () => {
    const selectedQuestionnaires: number[] = [];

    assignments.forEach((assignment) => {
      const selectedForAssignment =
        selectedItems[assignment.id]?.questionnaires || new Set();
      if (selectedItems[assignment.id]?.all) {
        // If all is selected, add all questionnaire assignment_ids
        assignment.questionnaires.forEach((questionnaire) => {
          selectedQuestionnaires.push(questionnaire.assignment_id);
        });
      } else {
        // Add only selected questionnaire assignment_ids
        assignment.questionnaires.forEach((questionnaire) => {
          if (selectedForAssignment.has(questionnaire.id)) {
            selectedQuestionnaires.push(questionnaire.assignment_id);
          }
        });
      }
    });

    return selectedQuestionnaires;
  };

  const handleCreateReport = async () => {
    try {
      setCreating(true);
      setError(null);

      const selectedQuestionnaires = getSelectedQuestionnaires();

      if (selectedQuestionnaires.length === 0) {
        setError("Debe seleccionar al menos un cuestionario");
        return;
      }

      const payload: CreateReportPayload = {
        patient_id: patientId,
        title: title.trim() || null,
        prompt: prompt.trim() || null,
        professional_notes: notes.trim() || null,
        questionnaire_assignments: selectedQuestionnaires,
      };

      const response = await fetchWithAuth("/reports", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (response.success) {
        onClose();
        onSuccess?.();
      } else {
        setError(response.message || "Error al crear el informe");
      }
    } catch (err) {
      setError("Error al crear el informe. Por favor, intente nuevamente.");
      console.error("Error creating report:", err);
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
      <div className="w-full max-w-3xl rounded-xl bg-white p-6 shadow-xl dark:bg-navy-800">
        <h2 className="mb-6 text-xl font-bold text-navy-700 dark:text-white">
          Crear Informe
        </h2>

        {/* Text inputs section */}
        <div className="mb-6 space-y-4">
          <div>
            <label
              htmlFor="title"
              className="mb-2 block text-sm font-medium text-gray-700 dark:text-white"
            >
              Título
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ingrese el título del informe"
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-navy-700 placeholder-gray-400 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:placeholder-gray-500"
            />
          </div>

          <div>
            <label
              htmlFor="prompt"
              className="mb-2 block text-sm font-medium text-gray-700 dark:text-white"
            >
              Prompt
            </label>
            <textarea
              id="prompt"
              rows={3}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Ingrese su prompt personalizado para generar el informe"
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-navy-700 placeholder-gray-400 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:placeholder-gray-500"
            />
          </div>

          <div>
            <label
              htmlFor="notes"
              className="mb-2 block text-sm font-medium text-gray-700 dark:text-white"
            >
              Notas del profesional
            </label>
            <textarea
              id="notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ingrese las notas necesarias para generar el informe"
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-navy-700 placeholder-gray-400 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:placeholder-gray-500"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex h-40 items-center justify-center">
            <div className="border-primary border-t-transparent h-8 w-8 animate-spin rounded-full border-4"></div>
          </div>
        ) : error ? (
          <div className="mb-4 rounded-lg bg-red-50 p-4 text-red-500 dark:bg-red-900/20">
            {error}
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto">
            <h3 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">
              Seleccione los cuestionarios a incluir
            </h3>
            <div className="space-y-4">
              {assignments.map((assignment) => (
                <div
                  key={assignment.id}
                  className="rounded-lg border border-gray-200 dark:border-navy-600"
                >
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center space-x-4">
                      <input
                        type="checkbox"
                        checked={selectedItems[assignment.id]?.all || false}
                        onChange={() =>
                          toggleAssignmentSelection(assignment.id)
                        }
                        className="h-5 w-5 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                      />
                      <div>
                        <p className="font-medium text-navy-700 dark:text-white">
                          Asignación del{" "}
                          {new Date(assignment.created_at).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-gray-500">
                          Completado:{" "}
                          {new Date(assignment.completed_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleAssignment(assignment.id)}
                      className="p-2 text-gray-500 hover:text-gray-700"
                    >
                      {expandedAssignments.has(assignment.id) ? (
                        <MdKeyboardArrowUp className="h-6 w-6" />
                      ) : (
                        <MdKeyboardArrowDown className="h-6 w-6" />
                      )}
                    </button>
                  </div>

                  {expandedAssignments.has(assignment.id) && (
                    <div className="border-t border-gray-200 bg-gray-50 p-4 dark:border-navy-600 dark:bg-navy-900">
                      <div className="space-y-2">
                        {assignment.questionnaires.map((questionnaire) => (
                          <div
                            key={questionnaire.id}
                            className="flex items-center space-x-3 rounded-lg bg-white p-3 shadow-sm dark:bg-navy-800"
                          >
                            <input
                              type="checkbox"
                              checked={
                                selectedItems[
                                  assignment.id
                                ]?.questionnaires.has(questionnaire.id) || false
                              }
                              onChange={() =>
                                toggleQuestionnaireSelection(
                                  assignment.id,
                                  questionnaire.id
                                )
                              }
                              className="h-5 w-5 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                            />
                            <div>
                              <p className="font-medium text-navy-700 dark:text-white">
                                {questionnaire.title}
                              </p>
                              <p className="text-sm text-gray-500">
                                Completado:{" "}
                                {new Date(
                                  questionnaire.completed_at
                                ).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end space-x-4">
          <button
            onClick={onClose}
            disabled={creating}
            className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
          >
            Cancelar
          </button>
          <button
            onClick={handleCreateReport}
            disabled={creating}
            className="flex items-center space-x-2 rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {creating ? (
              <>
                <div className="border-t-transparent h-4 w-4 animate-spin rounded-full border-2 border-white"></div>
                <span>Creando...</span>
              </>
            ) : (
              <span>Crear Informe</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateReportModal;
